import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
  SearchInput,
  SegmentedControl,
  StatTile,
  TrendChart,
  type DataTableSort,
  type TrendPoint,
  type TrendSeries,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { useTableSort } from '@/lib/use-table-sort';
import { filterRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  INTERVAL_LABELS,
  LIVE_REFRESH_MS,
  RANGES,
  periodLabel,
  rangeWindow,
  type RangeKey,
} from '@/lib/report-window';
import {
  PAGE_TRAFFIC,
  TRAFFIC_OPTIONS,
  readPageTypes,
  templateLabel,
  trafficSearch,
  useTrafficParam,
  type PageTraffic,
  type PageTypeRow,
  type PageTypesStats,
} from '@/lib/page-types';
import { eventsToMarkers, listAnalyticsEvents } from '@/lib/analytics-events';
import { KeyEventsPanel } from '@/components/key-events';
import { InfoTip, METRIC_INFO, METRIC_LABEL } from '@/components/metric-info';
import { PercentChange, PositionChange, positionLabel } from '@/components/page-trend';
import { useIntervalChoice } from '@/lib/use-interval-choice';
import { NoPaidDataState, PAID_COVERAGE, PaidCoverageNote, paidCoverage } from '@/components/paid-traffic';

/** GSC retains ~16 months, so the presets stop at 12 months. */
const TYPE_RANGE_KEYS: readonly RangeKey[] = ['7d', '30d', '90d', '12m'];

/** Sortable columns on the types table (client-side over the loaded set). */
/** A page type with the label the table shows, for the search. */
interface PageTypeSearchRow extends PageTypeRow {
  readonly label: string;
}

/** The fields the search box matches. */
const TYPE_SEARCH_FIELDS: ReadonlyArray<keyof PageTypeSearchRow> = ['label', 'template'];

const TYPE_SORT_FIELDS = {
  type: 'type',
  pages: 'pages',
  sessions: 'sessions',
  change: 'change',
  impressions: 'impressions',
  impressionsChange: 'impressionsChange',
  position: 'position',
  positionChange: 'positionChange',
  leads: 'leads',
  cvr: 'cvr',
} as const;
const TYPE_SORT_KEYS = Object.values(TYPE_SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: TYPE_SORT_FIELDS.sessions, direction: 'desc' };

/** Each column's sort value; null sorts last. Position sorts best (lowest) first when ascending. */
const TYPE_SORT_ACCESSORS: Readonly<Record<string, (type: PageTypeSearchRow) => number | string | null>> = {
  [TYPE_SORT_FIELDS.type]: (type) => type.label.toLowerCase(),
  [TYPE_SORT_FIELDS.pages]: (type) => type.pages,
  [TYPE_SORT_FIELDS.sessions]: (type) => type.sessions,
  [TYPE_SORT_FIELDS.change]: (type) => type.changePct,
  [TYPE_SORT_FIELDS.impressions]: (type) => type.impressions,
  [TYPE_SORT_FIELDS.impressionsChange]: (type) => type.impressionsChangePct,
  [TYPE_SORT_FIELDS.position]: (type) => type.avgPosition,
  [TYPE_SORT_FIELDS.positionChange]: (type) => type.positionChange,
  [TYPE_SORT_FIELDS.leads]: (type) => type.leads,
  [TYPE_SORT_FIELDS.cvr]: (type) => type.cvr,
};

/** What the per-type chart plots. */
const CHART_METRICS = {
  cvr: { label: METRIC_LABEL.conversionRate, unit: 'conversion rate' },
  sessions: { label: 'Sessions', unit: 'sessions' },
  leads: { label: 'Leads', unit: 'leads' },
} as const;
type ChartMetric = keyof typeof CHART_METRICS;

function cvrLabel(cvr: number | null): string {
  return cvr === null ? '—' : `${(cvr * 100).toFixed(2)}%`;
}

function compactCount(value: number): string {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/** CVR per bucket as a percent number; 0 when the bucket had no sessions. */
function cvrPercent(leads: number, sessions: number): number {
  return sessions > 0 ? Math.round((leads / sessions) * 10000) / 100 : 0;
}

/** One type's per-bucket values for the chosen metric. */
function typeValues(type: PageTypeRow, metric: ChartMetric): number[] {
  if (metric === 'sessions') return [...type.sessionsSeries];
  if (metric === 'leads') return [...type.leadsSeries];
  return type.sessionsSeries.map((sessions, index) => cvrPercent(type.leadsSeries[index] ?? 0, sessions));
}

/** The chart's per-type lines and the total-per-bucket points for the tooltip axis. */
function buildChart(
  stats: PageTypesStats,
  metric: ChartMetric,
): { points: TrendPoint[]; series: TrendSeries[] } {
  const totalsSessions = stats.buckets.map((_, index) =>
    stats.types.reduce((sum, type) => sum + (type.sessionsSeries[index] ?? 0), 0),
  );
  const totalsLeads = stats.buckets.map((_, index) =>
    stats.types.reduce((sum, type) => sum + (type.leadsSeries[index] ?? 0), 0),
  );
  const pointValue = (index: number): number => {
    if (metric === 'sessions') return totalsSessions[index] ?? 0;
    if (metric === 'leads') return totalsLeads[index] ?? 0;
    return cvrPercent(totalsLeads[index] ?? 0, totalsSessions[index] ?? 0);
  };
  const points = stats.buckets.map((bucket, index) => ({
    key: bucket,
    label: periodLabel(bucket, stats.range.interval),
    value: pointValue(index),
  }));
  const series = stats.types.map((type) => ({
    key: type.template || '(none)',
    label: templateLabel(type.template),
    values: typeValues(type, metric),
  }));
  return { points, series };
}

/**
 * Page-types analytics (admin-only): the site's traffic rolled up by page type
 * (program, category, blog-post, …) with sessions, on-page leads and CVR over
 * time. Global key events overlay every chart as vertical markers. Click a type
 * to drill into its pages.
 *
 * Note: the `form` type (e.g. /get-quote) is excluded — it converts by design
 * and would dwarf every landing type on the chart. Leads submitted on a shared
 * form attribute to that form, not the originating page type, so a landing
 * type's CVR here is a floor — surfaced in the UI.
 *
 * @returns The page-types overview.
 */
export function PageTypesAnalyticsPage(): React.ReactElement {
  const [range, setRange] = useState<RangeKey>('30d');
  const [metric, setMetric] = useState<ChartMetric>('cvr');
  const statsWindow = useMemo(() => rangeWindow(range), [range]);
  const [effectiveInterval, setIntervalChoice] = useIntervalChoice(RANGES[range].interval);
  const navigate = useNavigate();
  const { sort, onSort } = useTableSort(TYPE_SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();
  const { traffic, setTraffic } = useTrafficParam();
  const paid = traffic === PAGE_TRAFFIC.paid;

  const stats = useQuery({
    queryKey: ['page-types', statsWindow.from, statsWindow.to, effectiveInterval, traffic],
    queryFn: ({ signal }) =>
      readPageTypes({ from: statsWindow.from, to: statsWindow.to, interval: effectiveInterval, traffic }, signal),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });
  const events = useQuery({
    queryKey: ['analytics-events', statsWindow.from, statsWindow.to],
    queryFn: ({ signal }) => listAnalyticsEvents({ from: statsWindow.from, to: statsWindow.to }, signal),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });

  if (stats.isPending) return <LoadingState label="Loading page types…" />;
  if (stats.isError) {
    return <ErrorNotice title="Page types unavailable" message={errorMessage(stats.error)} />;
  }

  const data = stats.data;
  const markers = eventsToMarkers(events.data ?? []);
  const chart = buildChart(data, metric);
  const totalSessions = data.types.reduce((sum, type) => sum + type.sessions, 0);
  const totalLeads = data.types.reduce((sum, type) => sum + type.leads, 0);

  const activeSort = sort ?? DEFAULT_SORT;
  const accessor = TYPE_SORT_ACCESSORS[activeSort.key] ?? TYPE_SORT_ACCESSORS[TYPE_SORT_FIELDS.sessions]!;
  const rows = filterRows<PageTypeSearchRow>(
    data.types.map((type) => ({ ...type, label: templateLabel(type.template) })),
    search,
    TYPE_SEARCH_FIELDS,
  ).sort((a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    // No value (no baseline, never in search) sinks to the end either way.
    if (av === null || bv === null) return av === bv ? 0 : av === null ? 1 : -1;
    const cmp = typeof av === 'string' && typeof bv === 'string' ? av.localeCompare(bv) : Number(av) - Number(bv);
    return activeSort.direction === 'asc' ? cmp : -cmp;
  });
  const openType = (type: PageTypeRow) =>
    navigate(`/analytics/page-types/${encodeURIComponent(type.template)}${trafficSearch(traffic)}`);
  // Paid sessions exist only from the first sync (or backfill) that pulled them.
  const coverage = paidCoverage(traffic, data);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ANALYTICS"
        title="Page types"
        subtitle="How each kind of page converts — programs, categories, blog posts and the rest — with sessions, leads and conversion rate over time. Click a type to see its pages."
      />

      <div className="mb-4 flex min-w-0 flex-wrap gap-3">
        <SegmentedControl<PageTraffic> label="Traffic" value={traffic} onChange={setTraffic} options={TRAFFIC_OPTIONS} />
        <SegmentedControl<RangeKey>
          label="Range"
          value={range}
          onChange={(next) => {
            setRange(next);
          }}
          options={TYPE_RANGE_KEYS.map((key) => ({ key, label: RANGES[key].label, shortLabel: RANGES[key].shortLabel }))}
        />
        <SegmentedControl
          label="Interval"
          value={effectiveInterval}
          onChange={setIntervalChoice}
          options={(Object.keys(INTERVAL_LABELS) as (keyof typeof INTERVAL_LABELS)[]).map((key) => ({
            key,
            label: INTERVAL_LABELS[key],
          }))}
        />
      </div>
      {coverage === PAID_COVERAGE.partial && data.paidDataFrom !== null ? <PaidCoverageNote from={data.paidDataFrom} /> : null}

      {coverage === PAID_COVERAGE.none ? (
        <NoPaidDataState onShowAll={() => setTraffic(PAGE_TRAFFIC.all)} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {data.types.slice(0, 8).map((type) => (
              <StatTile
                key={type.template || '(none)'}
                label={templateLabel(type.template)}
                value={type.sessions.toLocaleString()}
                changePct={type.changePct}
                hint={`${cvrLabel(type.cvr)} conversion rate · ${type.leads.toLocaleString()} leads`}
              />
            ))}
          </div>

          <Panel className="mt-4 p-5">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-portal-card-title font-bold text-portal-ink">By type over time</h2>
                <p className="text-portal-micro text-portal-muted">
                  {INTERVAL_LABELS[effectiveInterval]} · {periodLabel(data.range.from, 'day')} –{' '}
                  {periodLabel(data.range.to, 'day')} (calendar days, UTC)
                </p>
              </div>
              <SegmentedControl<ChartMetric>
                label="Metric"
                value={metric}
                onChange={setMetric}
                options={(Object.keys(CHART_METRICS) as ChartMetric[]).map((key) => ({
                  key,
                  label: CHART_METRICS[key].label,
                }))}
              />
            </div>
            {totalSessions === 0 && totalLeads === 0 ? (
              <EmptyState
                title="No traffic in this range"
                message="Data lands after the first page-stats sync — try a wider range."
              />
            ) : (
              <TrendChart
                points={chart.points}
                series={chart.series}
                markers={markers}
                variant="line"
                title={`${CHART_METRICS[metric].label} by page type`}
                unit={CHART_METRICS[metric].unit}
                formatValue={metric === 'cvr' ? (value) => `${value}%` : compactCount}
              />
            )}
            <p className="mt-2 text-portal-micro text-portal-muted">
              Conversion rate = on-page leads ÷ sessions.{' '}
              {paid
                ? 'Paid counts Google Ads only: sessions Google Analytics credits to google / cpc, and leads that came in with a Google Ads click ID. Impressions, clicks and position are organic search either way. '
                : ''}
              The Forms type (for example /get-quote) is left out: it converts by design and would dwarf every landing type.
              A lead submitted on a shared form is credited to that form, not to the page the visitor came from, so a
              landing type's conversion rate here is a floor.
            </p>
          </Panel>

          <div className="mt-8 mb-3">
            <h2 className="text-portal-section font-bold text-portal-ink">Types in this range</h2>
            <p className="text-portal-micro text-portal-muted">
              Every page type with traffic in the range — click a row to see its pages.
            </p>
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <SearchInput
              label="Search page types"
              placeholder="Page type"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
          </div>
          {rows.length === 0 ? (
            <Panel>
              <EmptyState title="No page types match" message="Try a different search." />
            </Panel>
          ) : (
            <DataTable
              headers={[
                { label: 'Type', sortKey: TYPE_SORT_FIELDS.type },
                { label: 'Pages', sortKey: TYPE_SORT_FIELDS.pages },
                { label: 'Sessions', sortKey: TYPE_SORT_FIELDS.sessions },
                { label: 'Δ sessions', sortKey: TYPE_SORT_FIELDS.change, info: <InfoTip label="change" text={METRIC_INFO.change} /> },
                { label: 'Impressions', sortKey: TYPE_SORT_FIELDS.impressions, info: <InfoTip label="impressions" text={METRIC_INFO.impressions} /> },
                { label: 'Δ impressions', sortKey: TYPE_SORT_FIELDS.impressionsChange },
                { label: 'Avg position', sortKey: TYPE_SORT_FIELDS.position, info: <InfoTip label="average position" text={METRIC_INFO.position} /> },
                { label: 'Δ position', sortKey: TYPE_SORT_FIELDS.positionChange, info: <InfoTip label="position change" text={METRIC_INFO.positionChange} /> },
                { label: 'Leads', sortKey: TYPE_SORT_FIELDS.leads, info: <InfoTip label="leads" text={METRIC_INFO.leads} /> },
                {
                  label: METRIC_LABEL.conversionRate,
                  sortKey: TYPE_SORT_FIELDS.cvr,
                  info: <InfoTip label="conversion rate" text={paid ? METRIC_INFO.paidLeadRate : METRIC_INFO.leadRate} />,
                },
                '',
              ]}
              visuallyHiddenHeaders={[10]}
              rightAlignedHeaders={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
              sort={sort}
              onSort={onSort}
              minWidth={1100}
              cards={rows.map((type) => ({
                key: type.template || '(none)',
                title: type.label,
                subtitle: `${type.pages.toLocaleString()} ${type.pages === 1 ? 'page' : 'pages'}`,
                fields: [
                  { label: 'Sessions', value: <>{type.sessions.toLocaleString()} <PercentChange value={type.changePct} /></> },
                  { label: 'Impressions', value: <>{compactCount(type.impressions)} <PercentChange value={type.impressionsChangePct} /></> },
                  { label: 'Avg position', value: <>{positionLabel(type.avgPosition)} <PositionChange value={type.positionChange} previous={type.previousAvgPosition} /></> },
                  { label: METRIC_LABEL.leadsAndConversionRate, value: `${type.leads.toLocaleString()} · ${cvrLabel(type.cvr)}` },
                ],
                ...(type.template !== '' ? { onOpen: () => openType(type) } : {}),
              }))}
            >
              {rows.map((type) => {
                const clickable = type.template !== '';
                return (
                  <tr
                    key={type.template || '(none)'}
                    onClick={clickable ? () => openType(type) : undefined}
                    className={`border-t border-portal-line align-middle ${clickable ? 'cursor-pointer transition-colors hover:bg-portal-bg' : ''}`}
                  >
                    <td className="p-3 text-portal-small font-bold text-portal-ink">{type.label}</td>
                    <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{type.pages.toLocaleString()}</td>
                    <td className="p-3 text-right text-portal-small tabular-nums">{type.sessions.toLocaleString()}</td>
                    <td className="p-3 text-right text-portal-small">
                      <PercentChange value={type.changePct} />
                    </td>
                    <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{type.impressions.toLocaleString()}</td>
                    <td className="p-3 text-right text-portal-small">
                      <PercentChange value={type.impressionsChangePct} />
                    </td>
                    <td className="p-3 text-right text-portal-small tabular-nums">{positionLabel(type.avgPosition)}</td>
                    <td className="p-3 text-right text-portal-small">
                      <PositionChange value={type.positionChange} previous={type.previousAvgPosition} />
                    </td>
                    <td className="p-3 text-right text-portal-small tabular-nums font-bold text-portal-ink">{type.leads.toLocaleString()}</td>
                    <td className="p-3 text-right text-portal-small tabular-nums">{cvrLabel(type.cvr)}</td>
                    <td className="p-3 text-right">
                      {clickable ? <ChevronRightIcon className="ml-auto size-4 text-portal-muted" aria-hidden="true" /> : null}
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          )}
        </>
      )}

      <KeyEventsPanel />
    </section>
  );
}
